/**
 * 头像图片本地缓存（IndexedDB）
 * 将头像图片以 Blob 形式缓存到 IndexedDB，避免每次进入页面重复请求
 * 通过 avatarKey（图片唯一标识）判断图片是否有更新
 */

const DB_NAME = 'im_avatar_cache'
const DB_VERSION = 2
const STORE_NAME = 'avatars'

let dbPromise = null

function openDB() {
  if (dbPromise) return dbPromise
  dbPromise = new Promise((resolve, reject) => {
    try {
      const request = indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = (event) => {
        const db = event.target.result
        // 删除旧版 object store（v1 使用 url 作为 keyPath）
        if (db.objectStoreNames.contains(STORE_NAME)) {
          db.deleteObjectStore(STORE_NAME)
        }
        db.createObjectStore(STORE_NAME, { keyPath: 'key' })
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => {
        dbPromise = null
        reject(request.error)
      }
    } catch (e) {
      dbPromise = null
      reject(e)
    }
  })
  return dbPromise
}

/**
 * 从 IndexedDB 获取已缓存的头像 Blob
 * @param {string} avatarKey 图片唯一标识
 * @returns {Promise<Blob|null>}
 */
async function getAvatarBlob(avatarKey) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly')
    const request = tx.objectStore(STORE_NAME).get(avatarKey)
    request.onsuccess = () => resolve(request.result?.blob || null)
    request.onerror = () => reject(request.error)
  })
}

/**
 * 将头像 Blob 存入 IndexedDB
 * @param {string} avatarKey 图片唯一标识（作为 key）
 * @param {Blob} blob 图片 Blob 数据
 */
async function storeAvatarBlob(avatarKey, blob) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).put({ key: avatarKey, blob, timestamp: Date.now() })
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

/**
 * 从 IndexedDB 删除指定头像缓存
 * @param {string} avatarKey 图片唯一标识
 */
async function deleteAvatarBlob(avatarKey) {
  const db = await openDB()
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite')
    tx.objectStore(STORE_NAME).delete(avatarKey)
    tx.oncomplete = () => resolve()
    tx.onerror = () => reject(tx.error)
  })
}

/**
 * 下载头像图片并缓存到 IndexedDB，返回 object URL 供页面显示
 * @param {string} avatarKey 图片唯一标识（用于 IndexedDB 存储）
 * @param {string} avatarUrl 头像图片下载地址
 * @returns {Promise<string|null>} object URL 或 null（失败时）
 */
export async function fetchAndCacheAvatar(avatarKey, avatarUrl) {
  if (!avatarKey || !avatarUrl) return null
  try {
    const response = await fetch(avatarUrl, { mode: 'cors' })
    if (!response.ok) return null
    const blob = await response.blob()
    await storeAvatarBlob(avatarKey, blob)
    return URL.createObjectURL(blob)
  } catch (e) {
    console.error('[AvatarCache] 缓存头像失败:', avatarKey, e)
    return null
  }
}

/**
 * 从 IndexedDB 恢复头像，创建 object URL
 * @param {string} avatarKey 图片唯一标识
 * @returns {Promise<string|null>} object URL 或 null（未缓存）
 */
export async function restoreAvatar(avatarKey) {
  if (!avatarKey) return null
  try {
    const blob = await getAvatarBlob(avatarKey)
    if (blob) {
      return URL.createObjectURL(blob)
    }
    return null
  } catch (e) {
    console.error('[AvatarCache] 恢复头像失败:', avatarKey, e)
    return null
  }
}

/**
 * 删除旧头像缓存（avatarKey 变更时调用）
 * @param {string} avatarKey 旧的图片唯一标识
 */
export async function removeAvatarCache(avatarKey) {
  if (!avatarKey) return
  try {
    await deleteAvatarBlob(avatarKey)
  } catch (e) {
    console.error('[AvatarCache] 删除旧头像缓存失败:', avatarKey, e)
  }
}

// ========== 聊天图片缓存 ==========

/**
 * 从图片 URL 中提取稳定的缓存 key（去除签名等查询参数）
 * @param {string} url 图片 URL
 * @returns {string} 缓存 key，空字符串表示不可缓存
 */
export function getImageCacheKey(url) {
  if (!url || url.startsWith('blob:') || url.startsWith('data:')) return ''
  try {
    const u = new URL(url)
    return 'img:' + u.origin + u.pathname
  } catch {
    return ''
  }
}

/**
 * 下载图片并缓存到 IndexedDB，返回 object URL
 * @param {string} cacheKey 缓存 key（由 getImageCacheKey 生成）
 * @param {string} imageUrl 图片下载地址
 * @returns {Promise<string|null>} object URL 或 null（失败时）
 */
export async function fetchAndCacheImage(cacheKey, imageUrl) {
  if (!cacheKey || !imageUrl) return null
  try {
    const response = await fetch(imageUrl, { mode: 'cors' })
    if (!response.ok) return null
    const blob = await response.blob()
    await storeAvatarBlob(cacheKey, blob)
    return URL.createObjectURL(blob)
  } catch (e) {
    console.error('[ImageCache] 缓存图片失败:', e)
    return null
  }
}

/**
 * 从 IndexedDB 恢复图片，创建 object URL
 * @param {string} cacheKey 缓存 key
 * @returns {Promise<string|null>} object URL 或 null（未缓存）
 */
export async function restoreImage(cacheKey) {
  if (!cacheKey) return null
  try {
    const blob = await getAvatarBlob(cacheKey)
    if (blob) return URL.createObjectURL(blob)
    return null
  } catch (e) {
    console.error('[ImageCache] 恢复图片失败:', e)
    return null
  }
}
